import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ConfirmDialog } from './ConfirmDialog';
import { Link as LinkIcon, Image, PencilSimple, Trash, ShareNetwork } from '@phosphor-icons/react';

interface ClientPage {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
}

const monoFont = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.08)',
  borderStrong: 'rgba(247,246,242,0.25)',
  bgHover: 'rgba(247,246,242,0.06)',
  destructive: '#FF3F2D',
};

function slugify(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

interface AdminClientPagesProps {
  onSelectPage: (pageId: string) => void;
}

export function AdminClientPages({ onSelectPage }: AdminClientPagesProps) {
  const { session } = useAuth();
  const [pages, setPages] = useState<ClientPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const fetchPages = async () => {
    const { data } = await supabase.rpc('get_client_pages_safe');
    setPages((data as ClientPage[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchPages(); }, []);

  const addPage = async () => {
    if (!newName.trim() || !newPassword.trim() || !session) return;

    // Generate unique slug
    let slug = slugify(newName);
    const { data: existing } = await supabase.rpc('get_client_pages_safe');
    const existingSlugs = new Set((existing as any[] || []).map((p: any) => p.slug));
    let counter = 1;
    const baseSlug = slug;
    while (existingSlugs.has(slug)) {
      counter++;
      slug = `${baseSlug}-${counter}`;
    }

    const { data: inserted, error } = await supabase
      .from('client_pages')
      .insert({ user_id: session.user.id, name: newName.trim(), slug, password: newPassword.trim() } as any)
      .select('id')
      .single();

    if (error || !inserted) {
      console.error('Failed to create page:', error);
      return;
    }

    setNewName(''); setNewPassword(''); setShowAdd(false);
    fetchPages();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
    // Redirect to the new page
    onSelectPage((inserted as any).id);
  };

  const deletePage = async (id: string) => {
    await supabase.from('client_pages').delete().eq('id', id);
    fetchPages();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  const updatePage = async (id: string, name: string, password: string) => {
    const updateData: Record<string, string> = { name, slug: slugify(name) };
    if (password.trim()) updateData.password = password.trim();
    await supabase.from('client_pages').update(updateData).eq('id', id);
    setEditingId(null);
    fetchPages();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  const copyShareLink = async (slug: string, id: string) => {
    const url = `${window.location.origin}/aprovacao/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(c => (c === id ? null : c)), 2000);
    } catch { alert(url); }
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 13, fontWeight: 300, padding: '6px 10px',
    border: `1px solid rgba(247,246,242,0.15)`, backgroundColor: 'rgba(247,246,242,0.04)', color: C.fg, width: '100%',
  };

  if (loading) return <p style={{ fontFamily: monoFont, color: C.fgMuted }}>Carregando...</p>;

  return (
    <div>
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Remover página"
        description="Excluir esta página e todas as suas imagens/votos? Esta ação não pode ser desfeita."
        confirmLabel="Remover" cancelLabel="Cancelar" destructive
        onConfirm={() => { if (deleteTarget) deletePage(deleteTarget); }}
      />

      <div className="flex items-center justify-between mb-6">
        <h2 className="uppercase text-sm font-light tracking-wider" style={{ color: C.fg }}>PÁGINAS DE APROVAÇÃO</h2>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="uppercase text-xs py-1 px-3"
          style={{ border: `1px solid ${C.borderStrong}`, fontFamily: monoFont, color: C.fg, backgroundColor: 'transparent', cursor: 'pointer', transition: 'background-color 0.15s ease' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = C.bgHover)}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          {showAdd ? 'CANCELAR' : '+ PÁGINA'}
        </button>
      </div>

      {showAdd && (
        <div className="mb-6 p-4 flex flex-col gap-3" style={{ border: `1px dashed rgba(247,246,242,0.15)` }}>
          <input placeholder="Nome da página (ex: Nike Tênis SS26)" value={newName} onChange={(e) => setNewName(e.target.value)} style={inputStyle} />
          <input placeholder="Senha de acesso" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyle} />
          <button
            onClick={addPage}
            disabled={!newName.trim() || !newPassword.trim()}
            className="uppercase text-xs py-2 px-4 disabled:opacity-30 self-start"
            style={{ border: `1px solid ${C.borderStrong}`, fontFamily: monoFont, color: C.fg, backgroundColor: 'transparent', cursor: 'pointer' }}
          >
            CRIAR PÁGINA
          </button>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {pages.map((page) => (
          <div key={page.id} style={{ border: `1px solid ${C.border}`, padding: 24 }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {editingId === page.id ? (
                  <EditPageForm page={page} onSave={(n, p) => updatePage(page.id, n, p)} onCancel={() => setEditingId(null)} inputStyle={inputStyle} />
                ) : (
                  <>
                    <button
                      onClick={() => onSelectPage(page.id)}
                      style={{ fontFamily: monoFont, fontSize: 16, fontWeight: 300, color: '#F7F6F2', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', transition: 'opacity 0.15s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                      {page.name}
                    </button>
                    <p className="mt-1" style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>
                      /aprovacao/{page.slug} — senha: ••••••
                    </p>
                  </>
                )}
              </div>

              {editingId !== page.id && (
                <div className="flex items-center gap-3 shrink-0">
                  <CmsIconButton
                    icon={<ShareNetwork size={14} weight="thin" />}
                    label={copiedId === page.id ? 'COPIADO!' : 'COMPARTILHAR'}
                    onClick={() => void copyShareLink(page.slug, page.id)}
                  />
                  <CmsIconButton icon={<Image size={14} weight="thin" />} label="FOTOS" onClick={() => onSelectPage(page.id)} />
                  <CmsIconButton icon={<PencilSimple size={14} weight="thin" />} label="EDITAR" onClick={() => setEditingId(page.id)} />
                  <CmsIconButton icon={<Trash size={14} weight="thin" />} label="APAGAR" onClick={() => setDeleteTarget(page.id)} destructive />
                </div>
              )}
            </div>
          </div>
        ))}
        {pages.length === 0 && <p className="text-xs uppercase" style={{ fontFamily: monoFont, color: C.fgMuted }}>Nenhuma página ainda. Clique em "+ PÁGINA" para criar.</p>}
      </div>
    </div>
  );
}

function CmsIconButton({ icon, label, onClick, destructive }: {
  icon: React.ReactNode; label: string; onClick: () => void; destructive?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="uppercase flex items-center gap-1"
      style={{
        fontFamily: monoFont, fontSize: 12, fontWeight: 300,
        color: destructive ? '#FF3F2D' : 'rgba(247,246,242,0.6)',
        background: 'none', border: 'none', cursor: 'pointer',
        transition: 'color 0.15s ease',
        opacity: destructive ? 0.7 : 1,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.color = destructive ? '#FF3F2D' : '#F7F6F2';
        if (destructive) e.currentTarget.style.opacity = '1';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.color = destructive ? '#FF3F2D' : 'rgba(247,246,242,0.6)';
        if (destructive) e.currentTarget.style.opacity = '0.7';
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function EditPageForm({ page, onSave, onCancel, inputStyle }: { page: { name: string }; onSave: (name: string, password: string) => void; onCancel: () => void; inputStyle: React.CSSProperties }) {
  const [name, setName] = useState(page.name);
  const [password, setPassword] = useState('');

  return (
    <div className="flex flex-col gap-2">
      <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="Nome" />
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} placeholder="Nova senha (deixe vazio para manter)" />
      <div className="flex gap-2">
        <button onClick={() => onSave(name, password)} className="uppercase text-xs py-1 px-3" style={{ border: `1px solid rgba(247,246,242,0.25)`, fontFamily: monoFont, color: 'rgba(247,246,242,0.85)', backgroundColor: 'transparent', cursor: 'pointer' }}>SALVAR</button>
        <button onClick={onCancel} className="uppercase text-xs py-1 px-3" style={{ fontFamily: monoFont, color: C.fgMuted, backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>CANCELAR</button>
      </div>
    </div>
  );
}
